import {
  type BoxRegion,
  regionZoomScale,
  zoomDecision,
} from "components/Document/ReviewInteraction";
import { RefObject, useCallback, useEffect, useMemo, useRef } from "react";

import type { DocumentPreviewHighlight } from "./DocumentPreview";

/**
 * Keeping the framed region in view, which the viewer will not do for you.
 *
 * Its two controls both act on their own schedule — `jumpToPage` scrolls, and
 * `zoomTo` re-lays out the page, after each call returns. Measuring the outline
 * in the same breath as asking for either lands the reader where the box used
 * to be, and neither offers a callback for when it has finished. What this
 * holds is the sequencing that gets around that.
 */

/** Centring the outline, and knowing when it is safe to. */
interface CentredOutline {
  attach: (element: HTMLDivElement | null) => void;
  /** True once the page holding the frame has rendered. */
  isMounted: () => boolean;
  centre: (forFrame: string | null) => void;
  /** Say that a zoom was just asked for, so the centring after it is owed. */
  expectZoomCentre: (forFrame: string | null) => void;
  forget: () => void;
}

function useCentredOutline(frameKey: string | null): CentredOutline {
  /**
   * The framed region's own outline, once the page holding it has rendered.
   *
   * Scrolling the element rather than computing a target keeps this correct
   * through zoom: it sits at the region's position inside the page layer, so
   * wherever the viewer has put the page, the element is where the box is.
   */
  const outlineRef = useRef<HTMLDivElement | null>(null);
  /**
   * The frame already centred, so this happens once per change.
   *
   * The viewer re-renders its pages as the reader scrolls and zooms, which
   * remounts the outlines with them. Without this, every one of those remounts
   * would drag the view back to the box and the reader could never look away
   * from it.
   */
  const centredRef = useRef<string | null>(null);
  /**
   * The frame owed a second centring once our own zoom has been applied.
   *
   * `zoomTo` re-lays out the page and the outline moves with it, but it does so
   * after the call returns — so centring in the same breath measures the box
   * where it used to be. Only ever set for a zoom we asked for: a reviewer's own
   * zoom is theirs, and pulling them back to the box on it is not help.
   */
  const owedRef = useRef<string | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);

  const centre = useCallback((forFrame: string | null) => {
    const outline = outlineRef.current;
    if (outline == null) return;
    centredRef.current = forFrame;
    // Centred on both axes: a box in the right-hand column needs the horizontal
    // half as much as a box below the fold needs the vertical, and the viewer
    // scrolls in both.
    //
    // Instant rather than smooth. A smooth scroll runs over many frames, which
    // is long enough for the viewer's own scrolling — a page jump, a zoom — to
    // start midway and leave the reader between the two.
    outline.scrollIntoView({ block: "center", inline: "center", behavior: "auto" });
  }, []);

  const attach = useCallback(
    (element: HTMLDivElement | null) => {
      observerRef.current?.disconnect();
      observerRef.current = null;
      outlineRef.current = element;
      if (element == null) return;

      // The page holding the frame is often rendered after the frame was set —
      // it may not have existed to scroll to when the jump was issued. Centre
      // once it does, but only for a frame not centred yet, or every re-render
      // on scroll would drag the reader back.
      if (centredRef.current !== frameKey) centre(frameKey);

      if (typeof ResizeObserver === "undefined") return;
      // The resize is the only signal the viewer offers that it has finished
      // re-laying out: the zoom plugin returns before it has.
      const observer = new ResizeObserver(() => {
        const owed = owedRef.current;
        if (owed == null) return;
        owedRef.current = null;
        centre(owed);
      });
      observer.observe(element);
      observerRef.current = observer;
    },
    [frameKey, centre]
  );

  useEffect(() => () => observerRef.current?.disconnect(), []);

  return {
    attach,
    isMounted: useCallback(() => outlineRef.current != null, []),
    centre,
    expectZoomCentre: useCallback((forFrame: string | null) => {
      owedRef.current = forFrame;
    }, []),
    forget: useCallback(() => {
      centredRef.current = null;
      owedRef.current = null;
    }, []),
  };
}

export interface FramedRegion {
  /** The scrolling viewport, for measuring what a zoom has to fit into. */
  viewportRef: RefObject<HTMLDivElement | null>;
  /** Give this to the framed outline, and only that one. */
  attachFramedOutline: (element: HTMLDivElement | null) => void;
  /** Identifies the frame, so a change can be told from a re-render. */
  frameKey: string | null;
  /** Call when the document is ready; a jump before that is dropped. */
  onDocumentLoad: () => void;
  /** Call while rendering a page, with its intrinsic size at scale 1. */
  notePageSize: (size: { width: number; height: number } | null) => void;
  /** Call when the viewer reports a scale, whoever caused it. */
  noteScale: (scale: number) => void;
}

export function useFramedRegion({
  fileUrl,
  frame,
  zoomTo,
  jumpToPage,
}: {
  fileUrl: string;
  frame: DocumentPreviewHighlight | null | undefined;
  zoomTo: (scale: number) => void;
  /** Zero-based, as the viewer counts them. */
  jumpToPage: (pageIndex: number) => void;
}): FramedRegion {
  // The page to land on once the document is ready. A jump issued before the
  // pages exist is dropped, and the reader stays on page 1 of a bundle whose
  // manifest is on page 2 — the case this exists for.
  const loadedRef = useRef(false);
  const targetPage = frame?.page ?? null;

  /**
   * The framed region, identified by value rather than by object.
   *
   * `frame` is rebuilt whenever focus moves, so depending on it directly re-ran
   * the framing effect for every field the reviewer visited — including the
   * majority landing on a region identical to the last, because most of this
   * form is full-width bands shared by several boxes.
   */
  const regionX = frame?.region?.x ?? null;
  const regionY = frame?.region?.y ?? null;
  const regionWidth = frame?.region?.width ?? null;
  const regionHeight = frame?.region?.height ?? null;
  const frameKey =
    targetPage == null || regionX == null
      ? null
      : `${targetPage}:${regionX}:${regionY}:${regionWidth}:${regionHeight}`;

  const frameRegion: BoxRegion | null = useMemo(() => {
    if (regionX == null || regionY == null || regionWidth == null || regionHeight == null) {
      return null;
    }
    return { x: regionX, y: regionY, width: regionWidth, height: regionHeight };
  }, [regionX, regionY, regionWidth, regionHeight]);

  const outline = useCentredOutline(frameKey);

  const viewportRef = useRef<HTMLDivElement | null>(null);
  /** The page at scale 1, captured while rendering it. */
  const pageSizeRef = useRef<{ width: number; height: number } | null>(null);
  /**
   * The last scale we set ourselves.
   *
   * How we tell our own zoom from the reviewer's. If the current scale is not the
   * one we left, they have zoomed since, and following the caret must not
   * overrule that — help that undoes a deliberate action is not help.
   */
  const ourScaleRef = useRef<number | null>(null);
  const currentScaleRef = useRef<number | null>(null);

  const { forget } = outline;
  useEffect(() => {
    loadedRef.current = false;
    ourScaleRef.current = null;
    pageSizeRef.current = null;
    forget();
  }, [fileUrl, forget]);

  const frameNow = useCallback(() => {
    if (!loadedRef.current || targetPage == null) return;

    const page = pageSizeRef.current;
    const viewport = viewportRef.current;
    if (frameRegion != null && page != null && viewport != null) {
      const scale = zoomDecision({
        zoom: regionZoomScale({
          region: frameRegion,
          container: { width: viewport.clientWidth, height: viewport.clientHeight },
          page,
        }),
        current: currentScaleRef.current,
        ourLast: ourScaleRef.current,
      });
      if (scale != null) {
        ourScaleRef.current = scale;
        // Centred again once this has been applied, not now — the outline is
        // still where the old scale put it until the viewer re-lays out.
        outline.expectZoomCentre(frameKey);
        zoomTo(scale);
      }
    }

    // The page is already rendered, so the outline is on screen to scroll to —
    // and scrolling it reaches the page as well as the position. Jumping here
    // would fight this: it lands at the page's top-left, asynchronously, so its
    // scroll can arrive after ours and undo it.
    if (outline.isMounted()) {
      outline.centre(frameKey);
      return;
    }

    // Nothing to scroll to yet: the viewer only renders the pages near the
    // reader, and this frame's is not one of them. Jumping renders it, and the
    // outline centres itself when it mounts.
    jumpToPage(targetPage - 1);
  }, [targetPage, frameRegion, frameKey, outline, zoomTo, jumpToPage]);

  useEffect(frameNow, [frameNow]);

  return {
    viewportRef,
    attachFramedOutline: outline.attach,
    frameKey,
    onDocumentLoad: useCallback(() => {
      loadedRef.current = true;
      frameNow();
    }, [frameNow]),
    notePageSize: useCallback((size: { width: number; height: number } | null) => {
      pageSizeRef.current = size;
    }, []),
    noteScale: useCallback((scale: number) => {
      currentScaleRef.current = scale;
    }, []),
  };
}
